import { type ReactNode, useEffect } from "react";
import { type NumberFormatValues } from "react-number-format/types/types";
import { useField } from "formik";

import { MoneyInput } from "@vega-core/components/Form/Controls/Input/MoneyInput/MoneyInput";
import { type CoreCurrency } from "@vega-core/models/money";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

type Props = {
  currency?: CoreCurrency;
  clearable?: boolean;
  isRequired?: boolean;
  label?: string;
  labelTag?: ReactNode;
  name: string;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  constrainInputWidth?: boolean;
  allowAbbreviations?: boolean;
};

export function MoneyField({
  currency,
  clearable,
  isRequired,
  label,
  labelTag,
  name,
  className,
  disabled,
  fullWidth,
  constrainInputWidth = false,
  allowAbbreviations,
}: Props) {
  const [amountField, amountMeta, amountHelpers] = useField(`${name}.amount`);
  const [, parentMeta] = useField(name);
  const [currencyField, , currencyHelpers] = useField(`${name}.currency`);

  // Check nested error first (.amount), fallback to parent-level string error.
  // Some validation schemas may set errors at the parent object level
  // (e.g., when using Yup.lazy or conditional validation) rather than
  // on the nested .amount field, so we need to check both locations.
  let errorToDisplay: string | undefined;

  if (amountMeta.touched && isTruthy(amountMeta.error)) {
    errorToDisplay = amountMeta.error;
  } else if (parentMeta.touched && typeof parentMeta.error === "string") {
    errorToDisplay = parentMeta.error;
  } else {
    errorToDisplay = undefined;
  }

  const setMoney = (e: NumberFormatValues) => {
    amountHelpers.setValue(e.value);

    // Only set currency if form field is empty
    if (!currencyField.value) {
      if (currency) {
        currencyHelpers.setValue(currency);
      } else {
        currencyHelpers.setValue("USD");
      }
    }
  };

  useEffect(() => {
    if (amountField.value !== undefined && amountField.value !== "") {
      amountHelpers.setTouched(true, true);
    }
  }, [amountField.value]);

  return (
    <MoneyInput
      allowAbbreviations={allowAbbreviations}
      className={className}
      clearable={clearable}
      constrainInputWidth={constrainInputWidth}
      currency={(currencyField.value as CoreCurrency | undefined) ?? currency}
      disabled={disabled}
      error={errorToDisplay}
      fullWidth={fullWidth}
      isOptional={isFalsy(isRequired)}
      label={label}
      labelTag={labelTag}
      name={amountField.name}
      onBlur={() => amountHelpers.setTouched(true)}
      onClear={() => amountHelpers.setValue("")}
      onValueChange={setMoney}
      value={amountField.value ?? ""}
    />
  );
}
